import React, { SFC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    TextField as MaterialTextField,
    Checkbox,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    IconButton,
} from '@material-ui/core'
import { Form } from 'react-final-form'
import { TextField as RffTextField } from 'mui-rff'
import { UserModel, UserRfid } from '../../../shared/types/user'
import { ValidationErrors } from 'final-form'
import { useDispatch, useSelector } from 'react-redux'
import { addUser, editUser } from '../../../redux/actions/users'
import { setRequiredErrors } from '../../../utils/ui'
import { validateEmail } from '../../../shared/utils/validation'
import { DialogProgress } from '../../../shared/components/dialogProgress'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import { CountrySelect } from '../../../shared/components/countrySelect'
import { TagModel, TagUser } from '../../../shared/types/tag'
import { Autocomplete } from '@material-ui/lab'
import { config } from '../../../config'
import { Tenant } from '../../../shared/types/tenant'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'hidden',
    },
    rfidinput: {
        width: '235px',
    },
    rfidbutton: {
        position: 'relative',
        top: '10px',
        float: 'right',
    },
    accordionSummary: {
        height: 57,
        backgroundColor: "rgba(0, 0, 0, 0.09)",
        border: "1px solid transparent",
    },
    accordionLabel: {
        color: "inherit",
    },
}))

interface UserAddEditDialogProps {
    isAdd: boolean
    onClose(user?: UserModel): void
    initialModel?: UserModel
}
const UserAddEditDialog: SFC<UserAddEditDialogProps> = ({ isAdd, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const stateSelector = isAdd ? (state: any) => state.users.add : (state: any) => state.users.edit
    const [showHidden, setShowHidden] = useState<boolean>(false)
    // const [monthlyAllowanceEnabled, setMonthlyAllowanceEnabled] = useState<boolean>(
    //     (initialModel?.monthlyAllowance ?? 0) > 0
    // )
    const [monthlyAllowance, setMonthlyAllowance] = useState<number>(initialModel?.monthlyAllowance ?? 0)

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const tenantsState = useSelector((state: any) => state.tenants)
    const [userTenants, setUserTenants] = useState<string>(initialModel?.tenants || '')
    const [allTenants, setAllTenants] = useState<Tenant[]>([])

    const [rfids, setRfids] = useState<UserRfid[]>(initialModel?.rfids || [])
    // State to manage the input value
    const [inputValue, setInputValue] = useState<string>('')

    const [tenantsMapped, setTenantsMapped] = useState<Tenant[]>([])

    const mainTenantId = config.mainTenantId

    const allTags = useSelector((state: any) => state.tags.items.user.value)
    const [userTags, setUserTags] = useState<TagUser[]>(initialModel?.tags || [])
    const [userTagsMapped, setUserTagsMapped] = useState<TagModel[]>([])

    const handleShowHiddenChange = () => {
        setShowHidden(!showHidden)
    }

    const handleTagChange = (event: any, newValue: TagModel[]) => {
        setUserTagsMapped(newValue)

        const newUserTags: TagUser[] = newValue.map((tag) => ({
            tagId: tag.id,
            userId: initialModel?.id,
        }))

        setUserTags(newUserTags)
    }
    const handleTenantChange = (event: any, newValue: Tenant[]) => {
        setTenantsMapped(newValue)
        setUserTenants(newValue.map((tenant) => tenant.id).join(','))
    }

    const handleAddRfid = () => {
        if (inputValue.trim()) {
            const newRfid: UserRfid = { rfid: inputValue, userId: initialModel?.id }
            setRfids((prevRfids) => [...prevRfids, newRfid])
            setInputValue('')
        }
    }

    const handleRemoveRfid = (rfidToRemove: string) => {
        setRfids((prevRfids) => prevRfids.filter((rfid) => rfid.rfid !== rfidToRemove))
    }

    const setTenants = () => {
        if (tenantId !== mainTenantId) {
            if (isAdd) {
                return tenantId
            } else {
                // check if it is not already included
                if (initialModel && !initialModel?.tenants.includes(tenantId)) {
                    return (initialModel.tenants += ',' + tenantId)
                }
            }
        } else {
            if (isAdd && userTenants === '') {
                return config.chargeTenantId
            } else {
                return userTenants
            }
        }
    }

    useEffect(() => {
        if (initialModel && initialModel.tags && allTags) {
            // Filter allTags to select only those TagModel objects whose id matches TagCharger.tagId
            const selectedTags = allTags.filter((tag: TagModel) =>
                initialModel.tags?.some((tagUser: TagUser) => tag.id === tagUser.tagId)
            )
            setUserTagsMapped(selectedTags)
        }
    }, [initialModel, allTags])

    useEffect(() => {
        if (initialModel && initialModel.tenants && allTenants) {
            const userTenants = allTenants.filter((tenant: Tenant) => initialModel.tenants.includes(tenant.id))
            setTenantsMapped(userTenants)
        }
    }, [initialModel, allTenants])

    useEffect(() => {
        if (tenantsState && tenantsState.value) {
            const allTenants = tenantsState.value.filter((tenant: Tenant) => tenant.id !== mainTenantId)
            setAllTenants(allTenants)
        }
    }, [initialModel, tenantsState, mainTenantId])

    useEffect(() => {
        if (initialModel?.showHiddenLocation) {
            setShowHidden(initialModel.showHiddenLocation)
        }
    }, [initialModel])

    return (
        <Dialog className={classes.dialog} open={true}>
            <DialogProgress stateSelector={stateSelector} />
            <DialogTitle>{isAdd ? t('users.newUser') : t('users.editUser')}</DialogTitle>

            <Form
                onSubmit={async (model) => {
                    const newUserObj = {
                        ...model,
                        companyName: model.companyName ?? '',
                        companyId: model.companyId ?? '',
                        taxId: model.taxId ?? '',
                        vatId: model.vatId ?? '',
                        phone: model.phone ?? '',
                        monthlyAllowance: monthlyAllowance,
                        showHiddenLocation: showHidden,
                        tags: userTags,
                        tenants: setTenants(),
                        rfids,
                        cars: null,
                    }

                    const result: any = await dispatch(
                        isAdd ? addUser(newUserObj as UserModel) : editUser(newUserObj as UserModel)
                    )
                    if (result) {
                        onClose(result)
                    }
                }}
                initialValues={initialModel}
                validate={(model: any): ValidationErrors => {
                    const result: any = {}

                    let requiredFields = ['firstName', 'lastName', 'email']
                    if (isAdd) {
                        requiredFields = [...requiredFields, 'password', 'confirmedPassword']
                        if (model.password && model.confirmedPassword && model.password !== model.confirmedPassword) {
                            result.confirmedPassword = t('users.passwordsMustMatch')
                        }
                    }
                    if (!isAdd && initialModel?.paymentRequestId) {
                        requiredFields = [...requiredFields, 'street', 'zip', 'city', 'country']
                    }
                    setRequiredErrors(result, model, requiredFields, t('common.required'))
                    if (model.email && !validateEmail(model.email)) {
                        result.email = t('users.invalidEmail')
                    }
                    return result
                }}
                render={({ handleSubmit, form }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={6}>
                                    <RffTextField
                                        name="firstName"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 64,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('users.firstName')}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <RffTextField
                                        name="lastName"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 64,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('users.lastName')}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <RffTextField
                                        name="email"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 320,
                                        }}
                                        type="email"
                                        variant="filled"
                                        label={t('users.email')}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <RffTextField
                                        name="phone"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 32,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('users.phone')}
                                    />
                                </Grid>
                                {isAdd && (
                                    <>
                                        <Grid item xs={12}>
                                            <RffTextField
                                                name="password"
                                                fullWidth
                                                required
                                                inputProps={{
                                                    maxLength: 64,
                                                }}
                                                type="password"
                                                variant="filled"
                                                label={t('users.password')}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <RffTextField
                                                name="confirmedPassword"
                                                fullWidth
                                                required
                                                inputProps={{
                                                    maxLength: 64,
                                                }}
                                                type="password"
                                                variant="filled"
                                                label={t('users.confirmPassword')}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="address-content"
                                            className={classes.accordionSummary}
                                        >
                                            <Typography variant="subtitle1" className={classes.accordionLabel}>
                                                {t("common.address")}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={9}>
                                                    <RffTextField
                                                        name="street"
                                                        fullWidth
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        inputProps={{
                                                            maxLength: 256,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.street')}
                                                    />
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <RffTextField
                                                        name="zip"
                                                        fullWidth
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.zip')}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <RffTextField
                                                        name="city"
                                                        fullWidth
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        inputProps={{
                                                            maxLength: 256,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.city')}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <CountrySelect
                                                        name="country"
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        label={t('users.state')}
                                                        variant="filled"
                                                    ></CountrySelect>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="address-content"
                                            className={classes.accordionSummary}
                                        >
                                            <Typography variant="subtitle1" className={classes.accordionLabel}>
                                                {t("users.companyInformation")}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <RffTextField
                                                        name="companyName"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.companyName')}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <RffTextField
                                                        name="companyId"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.companyId')}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <RffTextField
                                                        name="taxId"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.taxId')}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <RffTextField
                                                        name="vatId"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.vatId')}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={allTags}
                                        getOptionLabel={(tag: TagModel) => tag.name}
                                        value={userTagsMapped}
                                        onChange={handleTagChange}
                                        // 1. Úprava zobrazenia už vybratých tagov (Chips)
                                        renderTags={(value: TagModel[], getTagProps) =>
                                            value.map((option: TagModel, index: number) => {
                                                const isExclusive = option.isExclusive; // Prispôsob presný názov vlastnosti (napr. IsExclusive)
                                                return (
                                                    <Chip
                                                        key={option.id}
                                                        label={option.name}
                                                        {...getTagProps({ index })}
                                                        style={{
                                                            margin: 4,
                                                            backgroundColor: isExclusive ? '#f1eaff' : undefined, // Jemné červené pozadie
                                                            color: isExclusive ? '#6100ED' : undefined,          // Tmavočervený text
                                                            border: isExclusive ? '1px solid #DBC4FF' : undefined,
                                                            fontWeight: isExclusive ? 'bold' : 'normal'
                                                        }}
                                                    />
                                                );
                                            })
                                        }
                                        // 2. Úprava zobrazenia možností v rozbaľovacom zozname (Dropdown)
                                        renderOption={(option: TagModel) => (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <span style={{ fontWeight: option.isExclusive ? 'bold' : 'normal' }}>
                                                    {option.name}
                                                </span>
                                                {option.isExclusive && (
                                                    <span style={{ fontSize: '11px', color: '#6100ED', backgroundColor: '#f1eaff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                        EXCLUSIVE
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        renderInput={(params) => (
                                            <MaterialTextField
                                                {...params}
                                                variant="filled"
                                                label="Tags"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={7}>
                                    <MaterialTextField
                                        className={classes.rfidinput}
                                        variant="filled"
                                        label="RFID"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value.trim())}
                                    />
                                    <Button
                                        className={classes.rfidbutton}
                                        variant="contained"
                                        color="primary"
                                        onClick={handleAddRfid}
                                        disabled={!inputValue.trim()}
                                    >
                                        {t('users.addToList')}
                                    </Button>
                                </Grid>
                                <Grid item xs={5} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MaterialTextField
                                        fullWidth
                                        type="number"
                                        variant="filled"
                                        inputProps={{ min: 1 }}
                                        label={t('users.monthlyAllowance')}
                                        value={monthlyAllowance}
                                        onChange={(e) => setMonthlyAllowance(Math.round(Number(e.target.value)))}
                                    />
                                    <Tooltip title='Monthly allowance in euros. Whole number only, no decimals. Leave empty or set to 0 for no limit.' arrow>
                                        <span style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}>
                                            <IconButton
                                                size="small"
                                                style={{
                                                    marginLeft: 4,
                                                    bottom: 1,
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    {rfids.map((rfid) => (
                                        <Chip
                                            key={rfid.id}
                                            label={rfid.rfid}
                                            onDelete={() => handleRemoveRfid(rfid.rfid)}
                                            style={{ margin: 4 }}
                                        />
                                    ))}
                                </Grid>
                                <Grid hidden={!(tenantId && tenantId === mainTenantId)} item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={allTenants}
                                        getOptionLabel={(tenant: Tenant) => tenant.name}
                                        value={tenantsMapped}
                                        onChange={handleTenantChange}
                                        renderInput={(params) => (
                                            <MaterialTextField
                                                {...params}
                                                variant="filled"
                                                label="Tenants"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                        <Checkbox
                                            name="showHiddenLocation"
                                            checked={showHidden}
                                            onChange={handleShowHiddenChange}
                                            style={{
                                                color: '#6100ED',
                                                marginBottom: '4px',
                                                paddingLeft: '0px',
                                                paddingBottom: '0px',
                                                paddingTop: '0px',
                                                paddingRight: '8px',
                                            }}
                                        />
                                        {t('users.showHiddenLocation')}
                                    </Typography>
                                </Grid>
                                {/* <Grid item xs={6}>
                                    <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                        <Checkbox
                                            checked={monthlyAllowanceEnabled}
                                            onChange={() => setMonthlyAllowanceEnabled(!monthlyAllowanceEnabled)}
                                            style={{
                                                color: '#6100ED',
                                                marginBottom: '4px',
                                                paddingLeft: '0px',
                                                paddingBottom: '0px',
                                                paddingTop: '0px',
                                                paddingRight: '8px',
                                            }}
                                        />
                                        {t('users.monthlyAllowance')}
                                    </Typography>
                                </Grid>
                                <>
                                    {monthlyAllowanceEnabled && (
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                variant="filled"
                                                inputProps={{ min: 1 }}
                                                label={t('users.monthlyAllowanceHint')}
                                                value={monthlyAllowance}
                                                onChange={(e) =>
                                                    setMonthlyAllowance(Math.round(Number(e.target.value)))
                                                }
                                            />
                                        </Grid>
                                    )}
                                </> */}
                            </Grid>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button
                                onClick={() => {
                                    onClose()
                                }}
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton
                                label={isAdd ? t('common.create') : t('common.change')}
                                stateSelector={stateSelector}
                            />
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default UserAddEditDialog
